import type { FunctionComponent } from 'react';

import { forTemplate } from '@trello/legacy-i18n';
import { currentLocale } from '@trello/locale';
import { Button } from '@trello/nachos/button';
import { RouterLink } from '@trello/router/router-link';

import { LoadingSpinner } from 'app/src/components/LoadingSpinner';
import { Navigation } from './Navigation';
import type { PowerUpsPageQuery } from './PowerUpsPageQuery.generated';
import { usePowerUpsPageQuery } from './PowerUpsPageQuery.generated';

import styles from './PowerUpsPage.less';

// eslint-disable-next-line @trello/assets-alongside-implementation
import noPowerUpsImage from 'resources/images/directory/empty-search.svg';
// eslint-disable-next-line @trello/assets-alongside-implementation
import defaultPowerUpIcon from 'resources/images/directory/icons/customIcon.png';
// eslint-disable-next-line @trello/assets-alongside-implementation
import defaultAppIcon from 'resources/images/directory/icons/keyIcon.png';
// eslint-disable-next-line @trello/assets-alongside-implementation
import tacoBouncer from 'resources/images/TacoBouncer.png';

type Member = NonNullable<PowerUpsPageQuery['member']>;
type Organization = Member['organizations'][number];
type Plugin = Organization['plugins'][number];

const format = forTemplate('app_management');

const PowerUpTile: FunctionComponent<{
  plugin: Plugin;
}> = ({ plugin }) => {
  let name = plugin.name || format('untitled');

  const defaultIcon = plugin?.iframeConnectorUrl
    ? defaultPowerUpIcon
    : defaultAppIcon;
  const powerUpIconUrl = plugin?.icon?.url || defaultIcon;

  if (plugin.listings?.length) {
    let listing = plugin.listings.find((l) => l.locale === currentLocale);
    if (!listing) {
      listing = plugin.listings[0];
    }
    name = listing.name;
  }

  return (
    <RouterLink
      className={styles.powerUp}
      href={`/power-ups/${plugin.id}/edit`}
    >
      <img
        className={styles.powerUpIcon}
        src={powerUpIconUrl}
        role="presentation"
        alt=""
      />
      <div className={styles.powerUpName}>{name}</div>
    </RouterLink>
  );
};

const OrganizationSection: FunctionComponent<{
  organization: Organization;
}> = ({ organization }) => {
  const { displayName, plugins } = organization;

  if (!plugins || plugins.length === 0) {
    return null;
  }

  const content = plugins.map((plugin) => (
    <PowerUpTile key={plugin.id} plugin={plugin} />
  ));

  return (
    <div className={styles.orgSection}>
      <h2 className={styles.orgName}>{displayName}</h2>
      <div className={styles.orgPowerUps}>{content}</div>
    </div>
  );
};

export const PowerUpsPage: FunctionComponent = () => {
  const { data, error, loading } = usePowerUpsPageQuery({
    // eslint-disable-next-line @trello/disallow-fetch-policies
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    waitOn: ['None'],
  });
  const member = data?.member;

  let content = null;

  if (loading) {
    content = <LoadingSpinner />;
  } else if (error || !member) {
    content = (
      <div className={styles.content}>{format('something-went-wrong')}</div>
    );
  } else {
    const orgs = member.organizations;
    if (!orgs.length) {
      content = (
        <div className={styles.emptyStateContainer}>
          <img
            alt=""
            role="presentation"
            className={styles.emptyStateImage}
            src={tacoBouncer}
          />
          <p>{format('no-teams')}</p>
        </div>
      );
    } else {
      const orgsWithPlugins = orgs.filter((org) => org.plugins?.length);

      let organizationSections;

      if (!orgsWithPlugins.length) {
        organizationSections = (
          <div className={styles.emptyStateContainer}>
            <img
              alt=""
              role="presentation"
              className={styles.emptyStateImage}
              src={noPowerUpsImage}
            />
            <p
              // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml -- FIXME
              dangerouslySetInnerHTML={{
                __html: format('no-power-ups'),
              }}
            />
          </div>
        );
      } else {
        organizationSections = orgsWithPlugins.map((organization) => (
          <OrganizationSection
            key={organization.id}
            organization={organization}
          />
        ));
      }

      content = (
        <>
          <div className={styles.header}>
            <h1 className={styles.title}>
              {format('power-ups-and-integrations')}
            </h1>
            <RouterLink href="/power-ups/admin/new">
              <Button appearance="primary">{format('new')}</Button>
            </RouterLink>
          </div>
          {organizationSections}
        </>
      );
    }
  }

  return (
    <>
      <Navigation />
      <div className={styles.content}>{content}</div>
    </>
  );
};
